import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useState } from "react";

// Define the type for the cache
type RowCache = Map<string, ReactElement | JSX.Element>;

// Define the context value type
interface DatatableRowCacheContextType {
  getRowFromCache: (id: string) => ReactElement | undefined;
  writeRowToCache: (id: string, row: ReactElement) => void;
  resetCache: (shouldReset?: (key: string) => boolean) => void;
}

// Create the context
const DatatableRowCacheContext = createContext<DatatableRowCacheContextType | undefined>(undefined);

// Provider component
interface DatatableRowCacheProviderProps {
  children: ReactNode;
}

export const DatatableRowCacheProvider: React.FC<DatatableRowCacheProviderProps> = ({ children }) => {
  const [cache, setCache] = useState<RowCache>(new Map());

  // Get a row from the cache
  const getRowFromCache = (id: string): ReactElement | undefined => {
    return cache.get(id);
  };

  // Write a row to the cache
  const writeRowToCache = (id: string, row: ReactElement): void => {
    setCache((prevCache) => {
      const newCache = new Map(prevCache);
      newCache.set(id, row);
      return newCache;
    });
  };

  // Reset the cache
  const resetCache = useCallback(
    (shouldReset?: (key: string) => boolean) => {
      setCache((prevCache) => {
        if (!shouldReset) {
          // If no predicate is provided, clear the entire cache
          return new Map();
        }

        // Create a new Map with only the keys that should not be reset
        const newCache = new Map(
          Array.from(prevCache.entries()).filter(
            ([key]) => !shouldReset(key), // Keep keys that should not be reset
          ),
        );

        return newCache;
      });
    },
    [setCache],
  );
  return (
    <DatatableRowCacheContext.Provider value={{ getRowFromCache, writeRowToCache, resetCache }}>
      {children}
    </DatatableRowCacheContext.Provider>
  );
};

// Hook to use the DatatableRowCache context
export const useDatatableRowCache = (): DatatableRowCacheContextType => {
  const context = useContext(DatatableRowCacheContext);
  if (!context) {
    throw new Error("useDatatableRowCache must be used within a DatatableRowCacheProvider");
  }
  return context;
};
